/*----------------------------------
- WEBSITE DOMAIN FILTER URL ADAPTER
----------------------------------*/

import type { ClientContext } from '@/client/context';
import { PRODUCT_PAGE_ROUTES } from '@/common/catalogs/appRoutesCatalog';
import { filterPresets } from '@/common/catalogs/domains/filterPresets';
import { appErrorReporter } from '@/common/libs/errorReporting';
import type { TInputRadar } from '@/common/types/domains/radar';
import {
    decodeRadarFiltersUrl as decodeSharedRadarFiltersUrl,
    encodeRadarFiltersUrl as encodeSharedRadarFiltersUrl,
    findRadarFiltersPreset,
} from '@uniquedomains/components/radar/urlCodec';
import type {
    EncodeRadarFiltersUrlOptions as SharedEncodeRadarFiltersUrlOptions,
    RadarFiltersUrlCodecConfig,
    RadarFiltersUrlPresetCatalog,
    RadarFiltersUrlState,
} from '@uniquedomains/components/radar/urlCodec';

/*----------------------------------
- TYPES
----------------------------------*/

type ClientContextWithRequest = ClientContext & {
    request?: {
        path?: string | null;
        url?: string | null;
    } | null;
};

type EncodeRadarFiltersUrlOptions = {
    relativeTo?: ClientContext;
    surface?: SharedEncodeRadarFiltersUrlOptions['surface'];
};

/*----------------------------------
- CONFIG
----------------------------------*/

export const WEBSITE_DOMAINS_BASE_PATH = '/domains';
export const PRODUCT_DOMAINS_BASE_PATH = PRODUCT_PAGE_ROUTES.domains;

const codecConfig = {
    allowedConditionOps: [
        'IN',
        'NOT_IN',
        'BETWEEN',
        '>=',
        '<=',
        '=',
        '!=',
        'CONTAINS',
        'NOT_CONTAINS',
        'STARTS_WITH',
        'ENDS_WITH',
    ],
    allowedSortFields: [
        'attractivity',
        'dealScore',
        'demand',
        'popularity',
        'brandability',
        'askPrice',
        'price',
        'renewal',
        'spread',
        'domain',
    ],
    baseUrl: 'https://unique.domains',
    filterPresets: filterPresets as RadarFiltersUrlPresetCatalog,
    invalidGroupedNodePolicy: 'reject',
    productDomainsBasePath: PRODUCT_DOMAINS_BASE_PATH,
    reportError: (error) => appErrorReporter.app.handleError(error),
    rulesShape: 'grouped',
    websiteDomainsBasePath: WEBSITE_DOMAINS_BASE_PATH,
} satisfies RadarFiltersUrlCodecConfig<'grouped'>;

/*----------------------------------
- HELPERS
----------------------------------*/

const resolveContextUrl = (context: ClientContext | undefined): string | null => {
    const request = context ? (context as ClientContextWithRequest).request : undefined;
    const url = request?.url?.trim();
    if (url) return url;

    const path = request?.path?.trim();
    return path || null;
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const findPreset = (filters: Pick<TInputRadar, 'prompt' | 'rules'>): string | undefined => {
    return findRadarFiltersPreset(filters, codecConfig);
};

export const decodeRadarFiltersUrl = (inputUrl: string): Partial<TInputRadar> => {
    return decodeSharedRadarFiltersUrl(inputUrl, codecConfig) as Partial<TInputRadar>;
};

export const encodeRadarFiltersUrl = (
    filters: Partial<TInputRadar>,
    options?: EncodeRadarFiltersUrlOptions,
): string => {
    const sharedOptions: SharedEncodeRadarFiltersUrlOptions = {
        relativeToUrl: resolveContextUrl(options?.relativeTo),
        surface: options?.surface,
    };

    return encodeSharedRadarFiltersUrl(filters as Partial<RadarFiltersUrlState>, sharedOptions, codecConfig);
};
